import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Clock, CheckCircle2, XCircle, AlertTriangle, LogOut, RefreshCw } from "lucide-react";

const STATUS_INFO = {
  pending: {
    icon: Clock, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200",
    label: "Verifica in corso",
    description: "La tua richiesta è stata ricevuta ed è in attesa di verifica da parte dell'amministratore Synappsi. Verrai contattato via email non appena il tuo account sarà attivato.",
  },
  verified: {
    icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200",
    label: "Professionista verificato",
    description: "Il tuo account è attivo. Puoi utilizzare tutte le funzioni della piattaforma.",
  },
  rejected: {
    icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200",
    label: "Verifica rifiutata",
    description: "La verifica del tuo account è stata rifiutata. Contatta l'amministratore Synappsi per maggiori dettagli o correggi i dati e riprova.",
  },
  suspended: {
    icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200",
    label: "Account sospeso",
    description: "Il tuo account è temporaneamente sospeso. Contatta l'amministratore Synappsi.",
  },
  expired: {
    icon: AlertTriangle, color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-200",
    label: "Verifica scaduta",
    description: "La tua verifica è scaduta. Contatta l'amministratore Synappsi per rinnovarla.",
  },
};

const ACTION_LABELS = {
  submitted: "Richiesta di verifica sottomessa",
  approved: "Verifica approvata",
  rejected: "Verifica rifiutata",
  suspended: "Account sospeso",
  info_requested: "Richieste informazioni aggiuntive",
  seeded: "Account creato dal sistema",
};

export default function VerificationPendingPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const justRegistered = location.state?.justRegistered;

  const status = user?.verification_status || "pending";
  const info = STATUS_INFO[status] || STATUS_INFO.pending;
  const Icon = info.icon;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="flex items-center justify-center mb-6">
          <img src="/logo.svg?v=20" alt="Synappsi" className="h-16 w-auto" />
        </div>

        <Card className="p-6 md:p-10 bg-white border-slate-200 rounded-2xl shadow-sm">
          <div className={`flex items-start gap-4 p-5 rounded-xl border ${info.bg} ${info.border}`}>
            <div className={`h-12 w-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 ${info.color}`}>
              <Icon className="h-6 w-6" strokeWidth={2.2} />
            </div>
            <div>
              <div className={`text-xs font-bold uppercase tracking-[0.2em] ${info.color}`}>
                Stato account
              </div>
              <h1 className="font-heading text-2xl font-extrabold text-slate-900 mt-1"
                  data-testid="verification-status-label">{info.label}</h1>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{info.description}</p>
            </div>
          </div>

          {justRegistered && (
            <div className="mt-4 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>Registrazione ricevuta. Ti invieremo un'email non appena la verifica sarà completata.</div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Info label="Nome" value={`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "—"} />
            <Info label="Email" value={user?.email} />
            <Info label="Professione" value={user?.profession?.replace(/_/g, " ") || "—"} />
            <Info label="Specializzazione" value={user?.specialization || "—"} />
            <Info label="Ordine" value={`${user?.order_province || "—"} · N° ${user?.order_number || "—"}`} />
            <Info label="Telefono" value={user?.phone || "—"} />
          </div>

          {status !== "verified" && (
            <div className="mt-6 border-t border-slate-200 pt-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">
                Storico verifica
              </p>
              <VerificationHistory userId={user?.id} />
            </div>
          )}

          <div className="mt-8 flex flex-col md:flex-row items-center gap-3">
            <Button onClick={() => refreshUser()} variant="outline" data-testid="refresh-status-btn"
                    className="w-full md:w-auto">
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna stato
            </Button>
            {status === "verified" && (
              <Button onClick={() => navigate("/dashboard")} data-testid="goto-dashboard-btn"
                      className="w-full md:w-auto bg-[#1E3A8A] hover:bg-[#1E40AF]">
                Vai alla dashboard
              </Button>
            )}
            <Button onClick={handleLogout} variant="ghost" data-testid="pending-logout-btn"
                    className="w-full md:w-auto md:ml-auto text-slate-600">
              <LogOut className="h-4 w-4 mr-2" /> Esci
            </Button>
          </div>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-4">
          Hai bisogno di aiuto?{" "}
          <a href="mailto:supporto@synappsi.it" className="text-[#1E3A8A] underline">supporto@synappsi.it</a>
        </p>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 truncate">{value || "—"}</div>
    </div>
  );
}

function VerificationHistory({ userId }) {
  const [history, setHistory] = React.useState(null);
  React.useEffect(() => {
    if (!userId) return;
    // history is embedded in user, refresh via /auth/me — quick approach
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setHistory([]))
      .catch(() => setHistory([]));
  }, [userId]);
  return (
    <div className="text-xs text-slate-500 italic">
      Lo storico verifica completo sarà mostrato qui a breve.
    </div>
  );
}
